import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// Alertas de "falta pagar comisión" y "falta entregar donación", calculadas
// en vivo desde las tablas reales. No hay tabla propia: es agregación.

export interface CommissionAlert {
  salespersonId: string;
  salespersonName: string;
  pendingAmount: number;
  commissionCount: number;
}

export interface DonationAlert {
  donationId: string;
  beneficiary: string;
  pendingAmount: number;
}

export interface PendingAlerts {
  commissions: CommissionAlert[];
  donations: DonationAlert[];
  totalCommissionPending: number;
  totalDonationPending: number;
}

export async function fetchPendingAlerts(): Promise<PendingAlerts> {
  if (!isSupabaseConfigured || !supabase) {
    return { commissions: [], donations: [], totalCommissionPending: 0, totalDonationPending: 0 };
  }

  try {
    const [commissionsRes, salespersonsRes, donationsRes] = await Promise.all([
      supabase.from('commissions').select('salesperson_id, commission_amount, is_paid'),
      supabase.from('salespersons').select('id, full_name'),
      supabase.from('donations').select('id, beneficiary, period_name, donation_generated, is_delivered'),
    ]);

    if (commissionsRes.error || salespersonsRes.error || donationsRes.error) {
      console.warn('[alerts] query failed');
      return { commissions: [], donations: [], totalCommissionPending: 0, totalDonationPending: 0 };
    }

    const spMap = new Map(
      ((salespersonsRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.full_name]),
    );

    // Comisiones pendientes agrupadas por vendedor.
    const commissionMap = new Map<string, CommissionAlert>();
    ((commissionsRes.data ?? []) as Array<Record<string, any>>).forEach((c) => {
      if (c.is_paid) return;
      const spId = c.salesperson_id ?? '';
      const name = spMap.get(spId) || 'Sin vendedor';
      const g = commissionMap.get(spId) || {
        salespersonId: spId,
        salespersonName: name,
        pendingAmount: 0,
        commissionCount: 0,
      };
      g.pendingAmount += Number(c.commission_amount ?? 0);
      g.commissionCount += 1;
      commissionMap.set(spId, g);
    });

    const commissions = Array.from(commissionMap.values()).sort(
      (a, b) => b.pendingAmount - a.pendingAmount,
    );

    // Donaciones no entregadas.
    const donations: DonationAlert[] = ((donationsRes.data ?? []) as Array<
      Record<string, any>
    >)
      .filter((d) => !d.is_delivered)
      .map((d) => ({
        donationId: d.id,
        beneficiary: d.beneficiary || d.period_name || 'Sin entidad',
        pendingAmount: Number(d.donation_generated ?? 0),
      }))
      .filter((d) => d.pendingAmount > 0)
      .sort((a, b) => b.pendingAmount - a.pendingAmount);

    const totalCommissionPending = commissions.reduce((s, c) => s + c.pendingAmount, 0);
    const totalDonationPending = donations.reduce((s, d) => s + d.pendingAmount, 0);

    return { commissions, donations, totalCommissionPending, totalDonationPending };
  } catch (err) {
    console.warn('[alerts] connection failed:', err);
    return { commissions: [], donations: [], totalCommissionPending: 0, totalDonationPending: 0 };
  }
}

// --- Alertas de post-venta para un agente de ventas ---
// Devuelve cuántas garantías, devoluciones y cambios pendientes están ligados
// a las ventas facturadas por un vendedor concreto (via sales.salesperson_id).

export interface PostSaleAlert {
  warranties: number;
  returns: number;
  exchanges: number;
}

export async function fetchAgentPostSaleAlerts(salespersonId: string): Promise<PostSaleAlert> {
  if (!isSupabaseConfigured || !supabase || !salespersonId) {
    return { warranties: 0, returns: 0, exchanges: 0 };
  }

  try {
    // Ventas del vendedor.
    const { data: sales } = await supabase
      .from('sales')
      .select('id')
      .eq('salesperson_id', salespersonId);

    const saleIds = ((sales ?? []) as Array<{ id: string }>).map((s) => s.id);
    if (saleIds.length === 0) return { warranties: 0, returns: 0, exchanges: 0 };

    const [warrantiesRes, returnsRes, exchangesRes] = await Promise.all([
      supabase
        .from('warranties')
        .select('id')
        .in('sale_id', saleIds)
        .in('review_status', ['pending', 'under_review']),
      supabase
        .from('returns')
        .select('id')
        .in('sale_id', saleIds)
        .in('status', ['pending', 'received']),
      supabase
        .from('exchanges')
        .select('id')
        .in('sale_id', saleIds)
        .eq('status', 'pending'),
    ]);

    return {
      warranties: (warrantiesRes.data ?? []).length,
      returns: (returnsRes.data ?? []).length,
      exchanges: (exchangesRes.data ?? []).length,
    };
  } catch (err) {
    console.warn('[alerts] post-sale query failed:', err);
    return { warranties: 0, returns: 0, exchanges: 0 };
  }
}

// --- Alertas proactivas: pagos rechazados por la pasarela ---
// El admin ve en la campana del panel cuántos pagos quedaron RECHAZADOS, con el
// motivo y un enlace directo al pedido, sin abrir pedido por pedido. Cubre:
//  - Sistecrédito en ventas DIRECTAS (el vendedor debe reintentar con otro método).
//  - ePayco en la TIENDA (el cobro con tarjeta no pasó).
export interface RejectedPaymentAlert {
  orderId: string;
  orderNumber: string;
  customerName: string;
  reason: string;
  createdAt: string;
  method: 'sistecredito' | 'payment_link';
}

export async function fetchRejectedPaymentAlerts(): Promise<RejectedPaymentAlert[]> {
  if (!isSupabaseConfigured || !supabase) return [];

  try {
    const { data: payments, error } = await supabase
      .from('payments')
      .select('order_id, method, status, rejection_reason, created_at')
      .in('method', ['sistecredito', 'payment_link'])
      .in('status', ['rejected', 'cancelled']);
    if (error || !payments) return [];

    const rows = payments as Array<Record<string, any>>;
    const orderIds = Array.from(
      new Set(rows.map((p) => p.order_id).filter((id): id is string => Boolean(id))),
    );
    if (orderIds.length === 0) return [];

    const { data: orders } = await supabase
      .from('orders')
      .select('id, order_number, channel, customer_id, status, created_at')
      .in('id', orderIds);
    const orderMap = new Map(
      ((orders ?? []) as Array<Record<string, any>>).map((o) => [o.id, o]),
    );

    // Solo pedidos que siguen cancelados (pendientes de acción). Sistecrédito se
    // limita a ventas DIRECTAS (paridad con el aviso del vendedor); ePayco es un
    // cobro de la TIENDA (canal ecommerce).
    const relevant = rows.filter((p) => {
      const o = orderMap.get(p.order_id);
      if (!o || o.status !== 'cancelled') return false;
      if (p.method === 'sistecredito') return o.channel === 'direct';
      return true;
    });

    // Dedup por pedido + pasarela, quedándonos con el intento rechazado más reciente.
    const byKey = new Map<string, Record<string, any>>();
    relevant.forEach((p) => {
      const key = `${p.order_id}:${p.method}`;
      const prev = byKey.get(key);
      if (!prev || String(p.created_at ?? '') >= String(prev.created_at ?? '')) {
        byKey.set(key, p);
      }
    });
    if (byKey.size === 0) return [];

    const customerIds = Array.from(
      new Set(
        Array.from(byKey.values())
          .map((p) => orderMap.get(p.order_id)?.customer_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    let custMap = new Map<string, string>();
    if (customerIds.length > 0) {
      const { data: customers } = await supabase
        .from('customers')
        .select('id, first_name, last_name')
        .in('id', customerIds);
      custMap = new Map(
        ((customers ?? []) as Array<Record<string, any>>).map((c) => [
          c.id,
          `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
        ]),
      );
    }

    return Array.from(byKey.values())
      .map((p) => {
        const o = orderMap.get(p.order_id);
        return {
          orderId: p.order_id,
          orderNumber: o?.order_number ?? '',
          customerName: custMap.get(o?.customer_id) ?? '',
          reason: p.rejection_reason ? String(p.rejection_reason) : '',
          createdAt: p.created_at ?? '',
          method: (p.method === 'sistecredito' ? 'sistecredito' : 'payment_link') as
            | 'sistecredito'
            | 'payment_link',
        };
      })
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  } catch (err) {
    console.warn('[alerts] rejected payment query failed:', err);
    return [];
  }
}

// --- Alertas de reconciliación: pagos en vuelo sin confirmar ---
// Cuando un pedido queda esperando confirmación de pago en línea más de la
// ventana de gracia, el barrido de reconciliación (cron) deja una alerta. Aquí
// se leen las alertas ABIERTAS para mostrarlas en la campana del panel.
export interface ReconciliationAlert {
  orderId: string;
  orderNumber: string;
  method: 'sistecredito' | 'payment_link';
  minutesWaiting: number;
  firstSeen: string;
}

export async function fetchReconciliationAlerts(): Promise<ReconciliationAlert[]> {
  if (!isSupabaseConfigured || !supabase) return [];

  try {
    const { data, error } = await supabase
      .from('payment_reconciliation_alerts')
      .select('order_id, order_number, method, minutes_waiting, first_seen')
      .eq('resolved', false)
      .order('first_seen', { ascending: false });
    if (error || !data) return [];

    return (data as Array<Record<string, any>>).map((r) => ({
      orderId: r.order_id,
      orderNumber: r.order_number ?? '',
      method: (r.method === 'sistecredito' ? 'sistecredito' : 'payment_link') as
        | 'sistecredito'
        | 'payment_link',
      minutesWaiting: Number(r.minutes_waiting ?? 0),
      firstSeen: r.first_seen ?? '',
    }));
  } catch (err) {
    console.warn('[alerts] reconciliation query failed:', err);
    return [];
  }
}